import { useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, VStack, Heading, Text, useToast, Container, Card, CardBody, Image, HStack, Link } from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const Login = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const result = await login(phoneNumber, password);

        if (result.success) {
            toast({
                title: "登入成功",
                status: "success",
                duration: 2000,
                isClosable: true,
            });
            // 路由轉導由 PublicRoute 或 AuthContext 狀態改變觸發，或者在這裡手動轉
            // 由於 PublicRoute 會自動檢查 user 狀態，這裡不需要特別做什麼，但為了保險起見：
            // navigate('/dashboard'); 
        } else {
            toast({
                title: "登入失敗",
                description: result.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
        setIsLoading(false);
    };

    return (
        <Box
            minH="100vh"
            w="100vw"
            bg="white"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            px={6}
            overflowY="auto"
            sx={{
                paddingTop: 'calc(env(safe-area-inset-top) + 24px)',
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)',
            }}
        >
            {/* 全版面登入表單（移除中間浮動卡片；桌面用 maxW 收斂寬度、置中） */}
            <Container maxW="480px" px={0} my="auto">
                    <Box>
                        <VStack spacing={6} as="form" onSubmit={handleSubmit} w="100%">
                            <Box textAlign="center">
                                <Image
                                    src="/logo-transparent.png"
                                    alt="School Logo"
                                    boxSize={{ base: "140px", md: "120px" }} // Bigger entry logo
                                    mx="auto"
                                    mb={3}
                                    filter="drop-shadow(0 4px 6px rgba(0,0,0,0.08))"
                                />
                                <Heading size={{ base: "xl", md: "lg" }} color="brand.800" fontWeight="extrabold">天籟徐薇補習班平台</Heading>
                                <Text color="gray.600" fontSize={{ base: "md", md: "sm" }}>數位接送 · 安全守護</Text>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel color="gray.700" fontWeight="bold" fontSize={{ base: "lg", md: "md" }}>手機號碼</FormLabel>
                                <Input
                                    type="tel"
                                    placeholder="0912345678"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    size="lg"
                                    h={{ base: "60px", md: "48px" }} // Taller input
                                    fontSize={{ base: "lg", md: "md" }}
                                    bg="whiteAlpha.800"
                                    borderColor="gray.300"
                                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                                    borderRadius="xl"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color="gray.700" fontWeight="bold" fontSize={{ base: "lg", md: "md" }}>密碼</FormLabel>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    size="lg"
                                    h={{ base: "60px", md: "48px" }} // Taller input
                                    fontSize={{ base: "lg", md: "md" }}
                                    bg="whiteAlpha.800"
                                    borderColor="gray.300"
                                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                                    borderRadius="xl"
                                />
                            </FormControl>

                            <Button
                                type="submit"
                                colorScheme="cyan"
                                bgGradient="linear(to-r, brand.500, brand.400)"
                                _hover={{ bgGradient: "linear(to-r, brand.600, brand.500)" }}
                                size="lg"
                                h={{ base: "60px", md: "48px" }} // Taller button
                                fontSize={{ base: "xl", md: "lg" }} // Bigger text
                                w="full"
                                isLoading={isLoading}
                                loadingText="登入中"
                                borderRadius="xl"
                                boxShadow="lg"
                                color="white"
                            >
                                登入
                            </Button>

                            <Text fontSize={{ base: "md", md: "sm" }} color="gray.600" mt={2}>
                                第一次使用？ <RouterLink to="/activate" style={{ color: '#3182ce', fontWeight: 'bold' }}>前往啟用帳號</RouterLink>
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                                忘記密碼？請聯繫補習班行政人員
                            </Text>

                            {/* Footer Links for App Store Compliance */}
                            <HStack spacing={4} mt={4} pt={4} borderTopWidth="1px" borderColor="gray.200" w="full" justify="center">
                                <Link as={RouterLink} to="/privacy" fontSize="xs" color="gray.400" _hover={{ color: 'brand.500' }}>
                                    隱私權政策
                                </Link>
                                <Text fontSize="xs" color="gray.300">|</Text>
                                <Link as={RouterLink} to="/terms" fontSize="xs" color="gray.400" _hover={{ color: 'brand.500' }}>
                                    服務條款
                                </Link>
                            </HStack>
                        </VStack>
                    </Box>
            </Container>
        </Box>
    );
};

export default Login;
